import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Box, Plus } from 'lucide-react';
import { useImportTrackingStore } from '../store/import-tracking-store';
import { ContainerStatusBadge, STATUS_CONFIG } from './container-status-badge';
import { ContainerFormDialog, type ContainerDefaults } from './container-form-dialog';
import { Progress } from '@/components/ui/progress';
import {
  CONTAINER_STATUS_UI_COLORS,
  CONTAINER_STATUS_TEXT_COLORS,
} from '../utils/container-colors';

interface ContainersTabProps {
  orderId: string;
  /** Lo que la orden ya sabe: no se le vuelve a pedir al usuario. */
  defaults?: ContainerDefaults;
}

export function ContainersTab({ orderId, defaults }: ContainersTabProps) {
  const navigate = useNavigate();
  const {
    containers,
    isLoadingContainers,
    fetchOrderContainers,
    fetchContainerTypes,
    containerTypes,
  } = useImportTrackingStore();

  const [showForm, setShowForm] = useState(false);

  useEffect(() => {
    fetchOrderContainers(orderId);
    if (containerTypes.length === 0) {
      fetchContainerTypes();
    }
  }, [orderId]);

  if (isLoadingContainers) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {Array.from({ length: 3 }).map((_, i) => (
          <Skeleton key={i} className="h-48 w-full rounded-lg" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">Contenedores ({containers.length})</h3>
        <Button onClick={() => setShowForm(true)} size="sm">
          <Plus className="w-4 h-4 mr-1" />
          Agregar Contenedor
        </Button>
      </div>

      {containers.length === 0 ? (
        <Card>
          <CardContent className="text-center py-12">
            <Box className="w-10 h-10 mx-auto text-muted-foreground mb-3" />
            <p className="text-muted-foreground">No hay contenedores. Agregue uno para empezar.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {containers.map((container) => {
            const ctType =
              container.containerType ||
              containerTypes.find((ct) => ct.id === container.containerTypeId);
            const StatusIcon = STATUS_CONFIG[container.status].icon;
            const stripeBg = CONTAINER_STATUS_UI_COLORS[container.status];
            const iconColor = CONTAINER_STATUS_TEXT_COLORS[container.status];

            return (
              <Card
                key={container.id}
                className="cursor-pointer hover:shadow-md transition-shadow overflow-hidden"
                onClick={() => navigate(`/trade/embarques/${orderId}/contenedores/${container.id}`)}
              >
                <div className="flex">
                  {/* Franja de color por status */}
                  <div className={`w-1.5 shrink-0 ${stripeBg}`} />

                  <CardContent className="p-4 space-y-3 flex-1">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-2">
                        <StatusIcon className={`w-4 h-4 ${iconColor}`} />
                        <div>
                          <p className="font-semibold">
                            {container.containerNumber || 'Sin número'}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {ctType?.name || 'Tipo desconocido'}
                          </p>
                        </div>
                      </div>
                      <ContainerStatusBadge status={container.status} />
                    </div>

                    <div className="space-y-1.5">
                      <div className="flex items-center justify-between text-xs text-muted-foreground">
                        <span>Llenado</span>
                        <span className="font-medium text-foreground">
                          {container.fillPercent.toFixed(1)}%
                        </span>
                      </div>
                      <Progress
                        value={container.fillPercent}
                        className={`h-2 ${container.fillPercent > 90 ? '[&>div]:bg-bloqueo' : '[&>div]:bg-info'}`}
                      />
                    </div>

                    <div className="flex items-center justify-between text-xs text-muted-foreground">
                      <span>{container._count?.items ?? 0} item(s)</span>
                      <span>
                        {container.usedVolumeCbm.toFixed(2)} CBM /{' '}
                        {container.usedWeightKg.toFixed(0)} kg
                      </span>
                    </div>
                  </CardContent>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      <ContainerFormDialog
        orderId={orderId}
        defaults={defaults}
        open={showForm}
        onOpenChange={setShowForm}
        onSuccess={() => fetchOrderContainers(orderId)}
      />
    </div>
  );
}
